// The room's Session View — Live's clip grid, in the web app's own palette.
//
// A column is a TRACK and a row is a row of clips you hit together. The screen
// is in two bands, and the order is deliberate:
//
//   MATERIAL — every distinct thing on the Set's timeline, once, packed up
//   each track's own column (lib/ghost-clips.ts). Most Sets that reach the
//   room were built entirely in the Arrangement and have no Session View at
//   all, so this is the part that makes the screen playable, and it comes
//   first for that reason. It is not a Session View the producer built, and
//   the band says so.
//
//   SCENES — the Set's actual Session View after it, keeping Live's own scene
//   numbers and names.
//
// Row NUMBERS are never reordered — `splitBands` is a render-time partition
// over `buildSceneGrid`'s rows, and `SceneRow.row` is still the slot index a
// launch acts on, so `sceneLaunchPlan` cannot tell the two bands apart.
//
// Launching is Live's, through `useLaunchControls`: a clip takes its lane from
// whatever was there, a row takes the WHOLE Set — every clip in it starts and
// every lane it leaves empty stops — and both land on the next line of the
// launch quantization, never where the pointer happened to click.
//
// Editing borrows from the touch grids people already know. Hold a pad (Note,
// Push) or flip the Edit switch (GarageBand's Live Loops) and a tap selects
// instead of launching; the bar under the grid then acts on the pad, the empty
// slot or the scene row. Double-tap a selected audio pad to open it in the
// clip editor. The decisions are lib/pad-ops.ts's; the operations are
// usePadOps's.
//
// Built to stay still. The room re-renders every frame its transport moves, so
// the screen is memoised on props that only change when something a person can
// see changes; and a playing pad's ring is moved by ONE animation-frame loop
// writing a CSS variable straight onto the pads that are lit — the grid never
// re-renders to animate.
//
// The pads scroll both ways in their own box, with the track headers frozen
// along the top and the scene column down the left, as Live freezes them: a
// phone scrolls a Set with twelve tracks and twenty rows without losing which
// column is which.

import type { MaterialPayload } from "@web-shared/lib/material-item";
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { CornerUpLeft, Pencil, Play, Square } from "lucide-react";
import { isAudible } from "@shared/mixer.js";
import { clipColor, type Lane, type LaneClip } from "@web-shared/lib/arrangement";
import { slotTapAction, splitBands, type SceneGrid, type SceneRow } from "@web-shared/lib/scene-grid";
import { cleanClipName, cleanTrackName } from "@/lib/track-name";
import { cn } from "@web-shared/lib/utils";
import { MATERIAL_DRAG_TYPE, MINE_DRAG_TYPE } from "@web-shared/lib/drag-types";
import { buzz, useHold } from "@web-shared/hooks/useHold";
import { DROP_SLOT_ATTR, LIFT_DROP, LIFT_LEAVE, LIFT_OVER, type LiftEvent } from "@web-shared/lib/lift-drag";
import { useIsCompact } from "@web-shared/hooks/useIsNarrow";
import { Chevron, useDisclosure } from "@web-shared/components/ui/collapsible";
import { drawEnvelope, prepareCanvas } from "@web-shared/lib/waveform";
import type { PadOps } from "@/account/hooks/usePadOps";
import type { PadSelection } from "@/lib/pad-ops";
import { PadActionBar } from "@/components/PadActionBar";

/** Launch timing, as the room's chooser offers it: Live's three, plus the
 * multi-bar values a loop-led Set wants. */
export type PadQuantize = "bar" | "beat" | "none" | number;

export const QUANTIZE_OPTIONS: { value: PadQuantize; label: string }[] = [
  { value: "none", label: "off" },
  { value: "beat", label: "1 beat" },
  { value: 2, label: "1/2 bar" },
  { value: "bar", label: "1 bar" },
  { value: 8, label: "2 bars" },
  { value: 16, label: "4 bars" },
  { value: 32, label: "8 bars" },
];

/** Everything the grid needs to play Live's way. */
export interface PadLaunchState {
  /** Lane id → the clip that has the lane. */
  playing: Record<string, string>;
  /** Lane id → a clip still sounding out a stop queued for a coming line.
   * Live keeps it lit and blinking until it actually stops. */
  stopping: Record<string, string>;
  /** Lanes the Session View has taken from their timeline clips. */
  overridden: ReadonlySet<string>;
  onLaunch: (clip: LaneClip, lane: Lane) => void;
  onLaunchScene: (row: number) => void;
  onStopLane: (laneId: string) => void;
  onStopAll: () => void;
  onBackToArrangement: (laneId?: string) => void;
  /** How far round its loop a lane's clip has got, and how long that loop
   * is — together, the ring on a playing pad. A launched lane with no
   * position has not reached its line yet: Live's queued clip. */
  positionSec: (laneId: string) => number | null;
  periodSec: (laneId: string) => number | null;
}

/** Where a pad's waveform comes from — the room's player. */
export interface PadWaveformSource {
  /** The part of the clip's object a launch loops, as `buckets` interleaved
   * min/max pairs; null until it has been fetched. */
  peaksFor: (clip: LaneClip, lane: Lane, buckets: number) => Float32Array | null;
  /** Fetch what `peaksFor` needs — asked once per pad shown. */
  request: (clip: LaneClip, lane: Lane) => void;
  /** Bumps as objects decode — the trigger to redraw. */
  version: number;
}

export interface PadMixerState {
  muted: ReadonlySet<string>;
  soloed: ReadonlySet<string>;
  onMute: (laneId: string, muted: boolean) => void;
  onSolo: (laneId: string, soloed: boolean) => void;
  onVolume: (laneId: string, volume: number) => void;
}

/** Editing, when the page allows it. Absent ⇒ a launch-only grid. */
export interface PadEditState {
  ops: PadOps;
  /** GarageBand's Edit Cells: on, a tap selects instead of launching. */
  editMode: boolean;
  onEditMode: (on: boolean) => void;
  /** Any press of a pad, launch or select — where a wide screen shows it in
   * the docked clip editor, as Live's Clip View follows the clip clicked. */
  onPadPress?: (clipId: string) => void;
  /** Material dragged off the shelf onto an empty slot of the Set's own
   * scenes: a piece of this project (a clip id) or one of your clips. */
  onDropToSlot?: (laneId: string, row: number, payload: MaterialPayload) => void;
}

/** Live's fader travel: 0.85 of the range is unity (shared/mixer.ts). */
const UNITY = 0.85;

/** Grid geometry. A track column at its desktop width is the whole of a phone,
 * so a phone gets a narrower one — enough that the next track is visibly
 * there, which is what tells you to swipe at all. */
const COL_W_DESKTOP = { min: 160, max: 220 };
const COL_W_MOBILE = { min: 116, max: 116 };
/** The scene-launch column, frozen at the left edge so a row keeps its name
 * however far across you scroll. Wide enough on a desktop to read a name. */
const SCENE_COL_W = 64;
const SCENE_COL_W_MOBILE = 36;

/** Two taps on a selected pad within this are a double-tap. */
const DOUBLE_TAP_MS = 350;

/** Whether a lane is heard at all right now — Live's gates: mute wins, and
 * a solo silences every unsoloed lane. */
function audible(laneId: string, mixer: Pick<PadMixerState, "muted" | "soloed">): boolean {
  return isAudible(mixer.muted.has(laneId), mixer.soloed.has(laneId), mixer.soloed.size > 0);
}

/**
 * The rings, moved without React. While anything is launched, one loop reads
 * each lit pad's position and writes it as `--p` (0..1 round the loop) on the
 * pad, and `data-queued` while its line has not come round yet. The pads read
 * both in CSS. Nothing ticks when the grid is idle.
 */
function useLaunchFrames(
  root: RefObject<HTMLElement | null>,
  active: boolean,
  launch: Pick<PadLaunchState, "positionSec" | "periodSec">,
): void {
  const latest = useRef(launch);
  latest.current = launch;
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const frame = (): void => {
      const el = root.current;
      if (el) {
        const { positionSec, periodSec } = latest.current;
        for (const pad of el.querySelectorAll<HTMLElement>("[data-live-lane]")) {
          const lane = pad.dataset.liveLane!;
          const position = positionSec(lane);
          const period = periodSec(lane);
          const queued = position === null;
          if (pad.dataset.queued !== String(queued)) pad.dataset.queued = String(queued);
          const p = !queued && period ? (position % period) / period : 0;
          pad.style.setProperty("--p", p.toFixed(4));
        }
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [active, root]);
}


// ---------------------------------------------------------------------------
// The Pads screen
// ---------------------------------------------------------------------------

export const SessionPads = memo(function SessionPads({
  grid,
  launch,
  mixer,
  quantize,
  onQuantize,
  waveform,
  edit,
}: {
  grid: SceneGrid;
  launch: PadLaunchState;
  mixer: PadMixerState;
  quantize: PadQuantize;
  onQuantize: (q: PadQuantize) => void;
  waveform?: PadWaveformSource;
  edit?: PadEditState;
}) {
  const { lanes } = grid;
  const anyPlaying = Object.keys(launch.playing).length > 0 || Object.keys(launch.stopping).length > 0;
  const { material, scenes } = splitBands(grid);
  const narrow = useIsCompact();
  const help = useDisclosure(false);
  const scroller = useRef<HTMLDivElement | null>(null);
  useLaunchFrames(scroller, anyPlaying, launch);
  const headerStrip = useRef<HTMLDivElement | null>(null);
  const sceneColumn = useRef<HTMLDivElement | null>(null);
  const edgeFade = useRef<HTMLDivElement | null>(null);
  useLinkedScroll(scroller, headerStrip, sceneColumn, edgeFade);

  const col = narrow ? COL_W_MOBILE : COL_W_DESKTOP;
  const sceneW = narrow ? SCENE_COL_W_MOBILE : SCENE_COL_W;
  const lanesTemplate = `repeat(${lanes.length}, minmax(${col.min}px, ${col.max}px))`;
  // A shorter pad on a phone: at 112px two rows filled the screen, and the
  // grid is only readable as a grid when you can see more than two of them.
  const padH = narrow ? 84 : 112;

  const ops = edit?.ops;
  const sel = ops?.selection ?? null;
  const editMode = edit?.editMode ?? false;

  // The handlers every pad shares, stable for the life of the grid so a pad
  // only re-renders when something about IT changes. They read the latest
  // state through a ref.
  const live = useRef({ launch, edit, lanes });
  live.current = { launch, edit, lanes };
  const lastTap = useRef<{ id: string; at: number } | null>(null);

  const select = useCallback((next: PadSelection) => {
    live.current.edit?.ops.hold(next);
  }, []);

  const tapPad = useCallback((clip: LaneClip, lane: Lane, row: number) => {
    const { launch: l, edit: e } = live.current;
    e?.onPadPress?.(clip.id);
    if (e?.editMode) {
      const now = performance.now();
      const selected = e.ops.selection?.kind === "pad" && e.ops.selection.clipId === clip.id;
      const again = lastTap.current?.id === clip.id && now - lastTap.current.at < DOUBLE_TAP_MS;
      lastTap.current = { id: clip.id, at: now };
      if (selected && again) {
        e.ops.edit();
        return;
      }
      e.ops.select({ kind: "pad", clipId: clip.id, laneId: lane.id, row });
      return;
    }
    const action = slotTapAction(clip, lane, l.playing);
    if (action === "inert") return;
    buzz();
    if (action === "stop") l.onStopLane(lane.id);
    else l.onLaunch(clip, lane);
  }, []);

  const tapSlot = useCallback((lane: Lane, row: number) => {
    const { launch: l, edit: e } = live.current;
    if (e?.editMode) {
      e.ops.select({ kind: "slot", laneId: lane.id, row });
      return;
    }
    // Live's clip-stop button lives in every empty slot of a playing track.
    if (l.playing[lane.id]) {
      buzz();
      l.onStopLane(lane.id);
    }
  }, []);

  const tapScene = useCallback((row: number) => {
    const { launch: l, edit: e } = live.current;
    if (e?.editMode) {
      e.ops.select({ kind: "scene", row });
      return;
    }
    buzz();
    l.onLaunchScene(row);
  }, []);

  const canEdit = edit !== undefined;

  // The pads' keys: only while the grid is editable and something is selected
  // (or a paste is waiting). Space stays the transport's (RoomPage).
  useEffect(() => {
    if (!canEdit) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.("input, textarea, select, [contenteditable], [aria-modal='true'], [role='dialog']")) return;
      const o = live.current.edit?.ops;
      if (!o?.selection) return;
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();
      if (e.key === "Escape") o.select(null);
      else if (mod && key === "c") o.copy();
      else if (mod && key === "x") o.cut();
      else if (mod && key === "v") o.paste();
      else if (mod && key === "d") o.duplicate();
      else if (mod) return;
      else if (e.key === "Delete" || e.key === "Backspace") o.remove();
      else if (e.key === "Enter" && o.can.edit) o.edit();
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit]);

  const selectedPad = sel?.kind === "pad" ? sel.clipId : null;
  const selectedSlot = sel?.kind === "slot" ? `${sel.laneId}:${sel.row}` : null;
  const selectedScene = sel?.kind === "scene" ? sel.row : null;

  /** One band's scene buttons, for the frozen column. */
  const sceneButtonsOf = (rows: SceneRow[], kind: "material" | "scene") =>
    rows.map((row, i) => (
      <SceneButton
        key={`${kind}-${row.row}`}
        row={row.row}
        label={kind === "material" ? `${i + 1}` : narrow ? shortSceneLabel(row.label) : row.label}
        ghost={kind === "material"}
        empty={row.cells.every((c) => c === null)}
        height={padH}
        selected={selectedScene === row.row}
        editable={canEdit}
        onTap={tapScene}
        onHold={select}
      />
    ));

  /** One band's rows: a cell per lane. */
  const rowsOf = (rows: SceneRow[], kind: "material" | "scene") =>
    rows.map((row) => (
      <Fragment key={`${kind}-${row.row}`}>
        {row.cells.map((clip, laneIndex) => {
          const lane = lanes[laneIndex]!;
          if (!clip) {
            return (
              <EmptySlot
                key={`${lane.id}:${row.row}`}
                lane={lane}
                row={row.row}
                height={padH}
                stoppable={!editMode && Boolean(launch.playing[lane.id])}
                selected={selectedSlot === `${lane.id}:${row.row}`}
                editable={canEdit}
                onTap={tapSlot}
                onHold={select}
                // Only the Set's own scenes hold slots; the timeline band is drawn.
                onDrop={kind === "scene" ? edit?.onDropToSlot : undefined}
              />
            );
          }
          const on = launch.playing[lane.id] === clip.id;
          const stopping = !on && launch.stopping[lane.id] === clip.id;
          return (
            <Pad
              key={clip.id}
              clip={clip}
              lane={lane}
              laneIndex={laneIndex}
              row={row.row}
              state={on ? "on" : stopping ? "stopping" : null}
              dimmed={!audible(lane.id, mixer)}
              height={padH}
              waveform={waveform}
              selected={selectedPad === clip.id}
              editMode={editMode}
              editable={canEdit}
              onTap={tapPad}
              onHold={select}
            />
          );
        })}
      </Fragment>
    ));

  const howItWorks = canEdit
    ? "Each column is a track, each row is a scene. Tap a pad to play it, tap a row's ▶ to play the whole scene. Hold an audio pad to open it in the clip editor. A held pad (or any pad, with Edit on) is selected too: then copy, paste, duplicate, rename or delete it."
    : "Each column is a track, each row is a scene. Tap a pad to play that clip. Tap a row to play the whole scene at once.";

  const selLane = sel && sel.kind !== "scene" ? lanes.find((l) => l.id === sel.laneId) ?? null : null;
  const selRow = sel ? grid.rows.find((r) => r.row === sel.row) ?? null : null;
  const playSelected = useCallback(() => {
    const { launch: l, edit: e, lanes: ls } = live.current;
    const s = e?.ops.selection;
    if (!s) return;
    if (s.kind === "scene") {
      l.onLaunchScene(s.row);
      return;
    }
    if (s.kind !== "pad") return;
    const lane = ls.find((x) => x.id === s.laneId);
    const clip = lane?.sessionClips.find((c) => c.id === s.clipId);
    if (!lane || !clip) return;
    if (l.playing[lane.id] === clip.id) l.onStopLane(lane.id);
    else l.onLaunch(clip, lane);
  }, []);
  const keepSelectedRow = useCallback(() => {
    const e = live.current.edit;
    if (!e || !selRow) return;
    const cells = selRow.cells.flatMap((c, i) => (c ? [{ clipId: c.id, laneId: lanes[i]!.id }] : []));
    e.ops.keepRow(cells);
    e.ops.select(null);
  }, [selRow, lanes]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 px-4 pb-2 pt-3 wide:px-6 wide:pb-4 wide:pt-5">
        <button
          type="button"
          onClick={help.toggle}
          aria-expanded={help.open}
          className="flex shrink-0 min-h-9 items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground"
        >
          <Chevron open={help.open} />
          <span className="hidden wide:inline">How pads work</span>
          <span className="wide:hidden">Help</span>
        </button>
        <div className="flex items-center gap-2">
          {edit && (
            <button
              type="button"
              aria-pressed={editMode}
              onClick={() => {
                edit.onEditMode(!editMode);
                if (editMode) edit.ops.select(null);
              }}
              title={editMode ? "Done editing — taps play pads again" : "Edit pads — taps select instead of playing"}
              className={cn(
                "flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold",
                editMode
                  ? "border-transparent bg-[#f2c230] text-black"
                  : "border-panelline bg-white/5 text-muted-foreground hover:text-foreground",
              )}
            >
              <Pencil className="size-3.5" />
              {editMode ? "Done" : "Edit"}
            </button>
          )}
          <label className="flex items-center gap-1.5 rounded-full border border-panelline bg-white/5 px-2.5 py-1 text-xs text-muted-foreground">
            <span className="hidden wide:inline">Launch on</span>
            <span className="wide:hidden" aria-hidden>Q</span>
            <select
              value={typeof quantize === "number" ? String(quantize) : quantize}
              onChange={(e) => onQuantize(parseQuantize(e.target.value))}
              title="When a tapped pad actually starts — Live's launch quantization"
              aria-label="Launch quantization"
              className="cursor-pointer appearance-none bg-transparent py-1 font-semibold text-white outline-none"
            >
              {QUANTIZE_OPTIONS.map((o) => (
                <option key={String(o.value)} value={String(o.value)} className="bg-panel text-white">
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <SessionMasterControls launch={launch} />
        </div>
      </div>
      {help.open && <p className="mb-3 max-w-[60ch] shrink-0 px-4 text-sm text-muted-foreground wide:px-6">{howItWorks}</p>}

      {/* Live's frozen edges, as separate panes rather than sticky cells: the
          track headers above and the scene column beside the pads, which
          scroll in their own box. Nothing ever slides under a frozen edge, so
          none of them needs a fill, and the room's glow shows through all of
          it. The panes follow the pads' scroll (useLinkedScroll). */}
      <div
        className="relative grid min-h-0 flex-1"
        style={{ gridTemplateColumns: "auto minmax(0, 1fr)", gridTemplateRows: "auto minmax(0, 1fr)" }}
        // A tap on the floor between pads clears the selection.
        onClick={(e) => {
          if ((e.target as HTMLElement).dataset.floor !== undefined) ops?.select(null);
        }}
      >
        <div data-floor />
        {/* Tracks past the right edge — off a phone, or under the material
            shelf — fade out there instead of stopping at a cut that reads as
            the last one. An overlay, not a mask on the scroller: a masked
            scroll box loses Safari's fast scrolling. useLinkedScroll shows it. */}
        <div
          ref={edgeFade}
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-room to-transparent opacity-0"
        />
        <div ref={headerStrip} className={cn("overflow-x-auto overflow-y-hidden overscroll-contain", NO_SCROLLBAR)}>
          <div
            data-floor
            className="grid min-w-max gap-2 pb-3 pr-4 pt-0.5 wide:gap-3 wide:pb-4 wide:pr-6"
            style={{ gridTemplateColumns: lanesTemplate }}
          >
            {lanes.map((lane, i) => (
              <TrackHeader key={lane.id} lane={lane} laneIndex={i} launch={launch} mixer={mixer} narrow={narrow} />
            ))}
          </div>
        </div>

        <div ref={sceneColumn} className={cn("overflow-y-auto overflow-x-hidden overscroll-contain", NO_SCROLLBAR)}>
          <div data-floor className="flex flex-col gap-2 pb-6 pl-4 pr-2 wide:gap-3 wide:pl-6 wide:pr-3" style={{ boxSizing: "content-box", width: sceneW }}>
            {material.length > 0 && <div style={{ height: BAND_H }} />}
            {sceneButtonsOf(material, "material")}
            {scenes.length > 0 && <div style={{ height: BAND_H }} />}
            {sceneButtonsOf(scenes, "scene")}
          </div>
        </div>
        <div
          ref={scroller}
          // No scroll-snap: the header strip drives this box by setting its
          // scrollLeft, and a snap container re-snaps every one of those, so
          // a finger on the track headers dragged the pads a column at a time.
          className="overflow-auto overscroll-contain"
          style={{ scrollbarWidth: "thin" }}
        >
          <div
            data-floor
            className="grid min-w-max gap-2 pb-6 pr-4 wide:gap-3 wide:pr-6"
            style={{ gridTemplateColumns: lanesTemplate }}
          >
            {material.length > 0 && (
              <BandHeader title="From the timeline" note="Every distinct clip on a track, once." />
            )}
            {rowsOf(material, "material")}

            {scenes.length > 0 && (
              <BandHeader title="Scenes" note="The Set's own Session View, with Live's numbers and names." />
            )}
            {rowsOf(scenes, "scene")}
          </div>
        </div>
      </div>

      {ops && (
        <PadActionBar
          ops={ops}
          ctx={{
            laneName: selLane?.name ?? null,
            sceneLabel: selRow
              ? selRow.ghost
                ? String(material.indexOf(selRow) + 1)
                : selRow.label
              : null,
            ghostRow: selRow?.ghost === true,
            playing: sel?.kind === "pad" && launch.playing[sel.laneId] === sel.clipId,
            onPlay: playSelected,
            onKeepRow: keepSelectedRow,
          }}
        />
      )}
      {ops?.note && !ops.selection && (
        <div role="status" aria-live="polite" className="shrink-0 border-t border-panelline bg-sunk px-4 py-2 text-xs text-[var(--changed)]">
          {ops.note}
        </div>
      )}
    </div>
  );
});

/** "Scene 2" in a 36px column is "Scen…"; the number is the part that
 * tells two rows apart. A name of the Set's own is left alone. */
function shortSceneLabel(label: string): string {
  return label.replace(/^scene\s+/i, "");
}

/** A frozen pane scrolls with the pads, never by its own bar. */
const NO_SCROLLBAR = "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

/** Keeps the frozen panes on the pads' scroll: the header strip on its left,
 * the scene column on its top — and the pads on theirs, when a finger or a
 * wheel scrolls a pane instead. Written straight onto the elements, like
 * useLaunchFrames: a scroll never re-renders the grid. Each pane gives up the
 * room of the pads' own scrollbar, so both reach the same far edge. */
function useLinkedScroll(
  body: RefObject<HTMLDivElement | null>,
  header: RefObject<HTMLDivElement | null>,
  column: RefObject<HTMLDivElement | null>,
  fade: RefObject<HTMLDivElement | null>,
): void {
  useEffect(() => {
    const b = body.current;
    const h = header.current;
    const c = column.current;
    if (!b || !h || !c) return;
    const set = (el: HTMLElement, axis: "scrollLeft" | "scrollTop", v: number) => {
      if (Math.abs(el[axis] - v) > 0.5) el[axis] = v;
    };
    const fromBody = () => {
      set(h, "scrollLeft", b.scrollLeft);
      set(c, "scrollTop", b.scrollTop);
      const f = fade.current;
      const more = b.scrollWidth - b.clientWidth - b.scrollLeft > 1 ? "1" : "0";
      if (f && f.style.opacity !== more) f.style.opacity = more;
    };
    const fromHeader = () => set(b, "scrollLeft", h.scrollLeft);
    const fromColumn = () => set(b, "scrollTop", c.scrollTop);
    const gutters = () => {
      h.style.marginRight = `${b.offsetWidth - b.clientWidth}px`;
      c.style.marginBottom = `${b.offsetHeight - b.clientHeight}px`;
      fromBody();
    };
    // A mouse wheel only turns one way. Over the header strip — which only
    // scrolls across — and over pads with no rows to scroll down to, it would
    // do nothing, and the tracks off the right edge could only be reached by
    // the thin scrollbar or Shift. There it scrolls across instead. A
    // trackpad's sideways swipe, Shift and pinch keep the browser's own.
    const sideways = (e: WheelEvent, always: boolean) => {
      if (e.ctrlKey || e.shiftKey || e.deltaX !== 0 || e.deltaY === 0) return;
      if (b.scrollWidth - b.clientWidth <= 1) return;
      if (!always && b.scrollHeight - b.clientHeight > 1) return;
      e.preventDefault();
      const unit = e.deltaMode === WheelEvent.DOM_DELTA_LINE ? 40 : e.deltaMode === WheelEvent.DOM_DELTA_PAGE ? b.clientWidth : 1;
      b.scrollLeft += e.deltaY * unit;
    };
    const wheelBody = (e: WheelEvent) => sideways(e, false);
    const wheelHeader = (e: WheelEvent) => sideways(e, true);
    gutters();
    const ro = new ResizeObserver(gutters);
    ro.observe(b);
    if (b.firstElementChild) ro.observe(b.firstElementChild);
    b.addEventListener("scroll", fromBody, { passive: true });
    h.addEventListener("scroll", fromHeader, { passive: true });
    c.addEventListener("scroll", fromColumn, { passive: true });
    b.addEventListener("wheel", wheelBody, { passive: false });
    h.addEventListener("wheel", wheelHeader, { passive: false });
    return () => {
      ro.disconnect();
      b.removeEventListener("scroll", fromBody);
      h.removeEventListener("scroll", fromHeader);
      c.removeEventListener("scroll", fromColumn);
      b.removeEventListener("wheel", wheelBody);
      h.removeEventListener("wheel", wheelHeader);
    };
  }, [body, header, column, fade]);
}

/** A band label's height. */
const BAND_H = 32;

/** A band's label, across the whole grid; the words stay pinned at the left
 * edge while the grid scrolls under them. */
function BandHeader({ title, note }: { title: string; note: string }) {
  return (
    <div className="flex items-center pt-2" style={{ gridColumn: "1 / -1", height: BAND_H }}>
      <div className="sticky left-0 z-10 flex max-w-[100vw] items-center gap-x-3 pr-3">
        <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{title}</span>
        {/* The phone has the same thing under "Help", and a line of it
            between every band is the screen the pads were supposed to get. */}
        <span className="hidden whitespace-nowrap text-[11px] text-muted-foreground wide:inline">{note}</span>
      </div>
      <span className="h-px min-w-6 flex-1 bg-panelline" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

/** Live's master Stop All Clips and Back to Arrangement, as one pair. Both
 * are disabled rather than hidden: they are the way out of the Session View,
 * and a control that appears and vanishes under the thumb cannot be found. */
export function SessionMasterControls({ launch, compact = false }: { launch: PadLaunchState; compact?: boolean }) {
  const anyPlaying = Object.keys(launch.playing).length > 0;
  const anyOverridden = launch.overridden.size > 0;
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        disabled={!anyPlaying}
        onClick={launch.onStopAll}
        aria-label="Stop all clips"
        title="Stop all clips — every launched clip stops on the next line"
        className={cn(
          "flex items-center gap-1.5 rounded-lg border border-panelline bg-panel font-semibold text-muted-foreground",
          "enabled:hover:bg-white/5 enabled:hover:text-foreground disabled:opacity-40",
          compact ? "h-6 px-2 text-[10px]" : "h-9 px-3 text-xs",
        )}
      >
        <Square className={cn("fill-current", compact ? "size-2.5" : "size-3")} />
        <span className="hidden wide:inline">Stop clips</span>
      </button>
      <button
        type="button"
        disabled={!anyOverridden}
        onClick={() => launch.onBackToArrangement()}
        aria-label="Back to Arrangement"
        title={
          anyOverridden
            ? `Back to Arrangement — ${launch.overridden.size} track${launch.overridden.size === 1 ? " is" : "s are"} playing the Session View instead of the timeline; return them all`
            : "Back to Arrangement — nothing has been taken from the timeline"
        }
        className={cn(
          "flex items-center gap-1.5 rounded-lg border font-semibold",
          anyOverridden
            ? "border-changed/50 bg-changed/15 text-changed hover:bg-changed/25"
            : "border-panelline bg-panel text-muted-foreground opacity-40",
          compact ? "h-6 px-2 text-[10px]" : "h-9 px-3 text-xs",
        )}
      >
        <CornerUpLeft className={cn(compact ? "size-2.5" : "size-3")} />
        <span className="hidden wide:inline">Arrangement</span>
      </button>
    </div>
  );
}

const TrackHeader = memo(function TrackHeader({
  lane,
  laneIndex,
  launch,
  mixer,
  narrow,
}: {
  lane: Lane;
  laneIndex: number;
  launch: PadLaunchState;
  mixer: PadMixerState;
  narrow: boolean;
}) {
  const isMuted = mixer.muted.has(lane.id);
  const isSoloed = mixer.soloed.has(lane.id);
  const color = clipColor(lane.sessionClips[0]?.color ?? lane.clips[0]?.color ?? 0, laneIndex);
  const playing = Boolean(launch.playing[lane.id] ?? launch.stopping[lane.id]);
  const overridden = launch.overridden.has(lane.id);

  const ms = (size: string) => (
    <>
      <button
        type="button"
        onClick={() => mixer.onMute(lane.id, !isMuted)}
        aria-pressed={isMuted}
        title={`${isMuted ? "Unmute" : "Mute"} ${lane.name}`}
        className={cn(
          "flex shrink-0 items-center justify-center rounded-md border text-[11px] font-bold",
          size,
          isMuted ? "border-mute bg-mute text-mute-ink" : "border-controlline bg-control text-muted-foreground",
        )}
      >M</button>
      <button
        type="button"
        onClick={() => mixer.onSolo(lane.id, !isSoloed)}
        aria-pressed={isSoloed}
        title={`${isSoloed ? "Unsolo" : "Solo"} ${lane.name}`}
        className={cn(
          "flex shrink-0 items-center justify-center rounded-md border text-[11px] font-bold",
          size,
          isSoloed ? "border-solo bg-solo text-solo-ink" : "border-controlline bg-control text-muted-foreground",
        )}
      >S</button>
    </>
  );

  return (
    <div
      className={cn(
        "rounded-xl border border-white/[0.06] bg-raised/40",
        narrow ? "p-2" : "p-3",
        !audible(lane.id, mixer) && "opacity-50",
      )}
    >
      <div className="flex items-center gap-2">
        <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
        <span className={cn("truncate font-semibold", narrow ? "text-xs" : "text-sm")} title={lane.name}>
          {cleanTrackName(lane.name)}
        </span>
        {/* A phone's per-track stop: the one thing a thumb wants from the
            header while the lane plays. */}
        {narrow && playing && (
          <button
            type="button"
            onClick={() => launch.onStopLane(lane.id)}
            aria-label={`Stop ${lane.name}`}
            className="-my-1 ml-auto flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground"
          >
            <Square className="size-2.5 fill-current" />
          </button>
        )}
      </div>
      {!narrow && <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{lane.kind === "midi" ? "MIDI" : "Audio"}</div>}
      {/* Desktop: M S + fader inline. Mobile: fader, then M S below. */}
      <div className={cn("flex items-center gap-1.5", narrow ? "mt-1" : "mt-2")}>
        {!narrow && <div className="flex items-center gap-1.5">{ms("size-[22px]")}</div>}
        <input
          type="range"
          min={0}
          max={100}
          defaultValue={Math.round((lane.volume * 100) / UNITY)}
          onChange={(e) => mixer.onVolume(lane.id, (Number(e.target.value) / 100) * UNITY)}
          className={cn(
            "h-6 min-w-0 flex-1 appearance-none bg-transparent accent-white",
            "[&::-moz-range-track]:h-1 [&::-moz-range-track]:rounded [&::-moz-range-track]:bg-controlline",
            "[&::-webkit-slider-runnable-track]:h-1 [&::-webkit-slider-runnable-track]:rounded [&::-webkit-slider-runnable-track]:bg-controlline",
            "[&::-webkit-slider-thumb]:-mt-1 [&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-foreground",
          )}
          aria-label={`${lane.name} volume`}
        />
      </div>
      {narrow && <div className="mt-1 flex items-center gap-1.5">{ms("size-7")}</div>}
      {/* Per-track stop / Back to Arrangement — desktop */}
      <div className={cn(narrow && "hidden", (playing || overridden) ? "mt-2" : "")}>
        {playing ? (
          <button
            type="button"
            onClick={() => launch.onStopLane(lane.id)}
            title={`Stop ${lane.name} — on the next line`}
            className="flex h-[26px] w-full items-center justify-center gap-1.5 rounded-md bg-primary text-[10px] font-semibold text-primary-foreground"
          >
            <Square className="size-2.5 fill-current" />
            stop
          </button>
        ) : overridden ? (
          <button
            type="button"
            onClick={() => launch.onBackToArrangement(lane.id)}
            title={`Back to Arrangement — ${lane.name} plays its timeline clips again`}
            className="flex h-[26px] w-full items-center justify-center gap-1.5 rounded-md border border-changed/40 bg-changed/10 text-[10px] font-semibold text-changed"
          >
            <CornerUpLeft className="size-2.5" />
            arrangement
          </button>
        ) : null}
      </div>
    </div>
  );
});

/** Shared by every touchable cell: no callout, no text selection, no
 * double-tap zoom — a pad is an instrument, not a document. */
const TOUCH = "select-none touch-manipulation [-webkit-touch-callout:none] [-webkit-tap-highlight-color:transparent]";

const SceneButton = memo(function SceneButton({
  row,
  label,
  ghost,
  empty,
  height,
  selected,
  editable,
  onTap,
  onHold,
}: {
  row: number;
  label: string;
  ghost: boolean;
  empty: boolean;
  height: number;
  selected: boolean;
  editable: boolean;
  onTap: (row: number) => void;
  onHold: (sel: PadSelection) => void;
}) {
  const hold = useHold(() => onHold({ kind: "scene", row }));
  const press = editable ? hold : null;
  return (
    <button
      type="button"
      onPointerDown={press?.onPointerDown}
      onPointerMove={press?.onPointerMove}
      onPointerUp={press?.onPointerUp}
      onPointerCancel={press?.onPointerCancel}
      onPointerLeave={press?.onPointerLeave}
      onContextMenu={press?.onContextMenu}
      onClick={() => {
        if (press?.consumeHold()) return;
        onTap(row);
      }}
      title={
        empty
          ? `Scene ${label} — empty, so launching it stops every track`
          : ghost
            ? `Launch row ${label} — loops taken from the timeline, not a scene Live has`
            : `Launch scene ${label} — every track takes this row; the ones it leaves empty stop`
      }
      aria-label={`Launch ${ghost ? "row" : "scene"} ${label}`}
      aria-pressed={editable ? selected : undefined}
      className={cn(
        "flex flex-col items-center justify-center gap-1 text-muted-foreground/60 transition-colors hover:text-white",
        TOUCH,
        ghost && "text-muted-foreground/45",
        selected && "text-white outline outline-2 -outline-offset-2 outline-white/70",
      )}
      style={{ height }}
    >
      <Play className="size-3 fill-current" />
      <span className="max-w-[90%] truncate font-mono text-[9px]">{label}</span>
    </button>
  );
});

const EmptySlot = memo(function EmptySlot({
  lane,
  row,
  height,
  stoppable,
  selected,
  editable,
  onTap,
  onHold,
  onDrop,
}: {
  lane: Lane;
  row: number;
  height: number;
  /** The lane is playing: an empty slot is Live's clip-stop button. */
  stoppable: boolean;
  selected: boolean;
  editable: boolean;
  onTap: (lane: Lane, row: number) => void;
  onHold: (sel: PadSelection) => void;
  onDrop?: PadEditState["onDropToSlot"];
}) {
  const hold = useHold(() => onHold({ kind: "slot", laneId: lane.id, row }));
  const press = editable ? hold : null;
  const [over, setOver] = useState(false);
  // A drag that ends anywhere (dropped elsewhere, or cancelled) clears it.
  useEffect(() => {
    if (!over) return;
    const clear = () => setOver(false);
    window.addEventListener("dragend", clear);
    window.addEventListener("drop", clear);
    return () => {
      window.removeEventListener("dragend", clear);
      window.removeEventListener("drop", clear);
    };
  }, [over]);
  // A thumb's drag off the shelf (lib/lift-drag.ts): DOM events, routed to
  // the same `onDrop` the HTML5 path calls.
  const ref = useRef<HTMLButtonElement>(null);
  const liftDrop = useRef(onDrop);
  liftDrop.current = onDrop;
  useEffect(() => {
    const el = ref.current;
    if (!el || !onDrop) return;
    const on = (e: Event) => {
      const { type, detail } = e as LiftEvent;
      if (type === LIFT_OVER) setOver(true);
      else if (type === LIFT_LEAVE) setOver(false);
      else {
        setOver(false);
        liftDrop.current?.(lane.id, row, detail.payload);
      }
    };
    for (const t of [LIFT_OVER, LIFT_LEAVE, LIFT_DROP]) el.addEventListener(t, on);
    return () => {
      for (const t of [LIFT_OVER, LIFT_LEAVE, LIFT_DROP]) el.removeEventListener(t, on);
    };
  }, [onDrop !== undefined, lane.id, row]);
  const accepts = (types: readonly string[]) => types.includes(MATERIAL_DRAG_TYPE) || types.includes(MINE_DRAG_TYPE);
  return (
    <button
      ref={ref}
      type="button"
      {...(onDrop ? { [DROP_SLOT_ATTR]: `${lane.id}:${row}` } : {})}
      onDragOver={
        onDrop
          ? (e) => {
              if (!accepts(e.dataTransfer.types)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
              setOver(true);
            }
          : undefined
      }
      onDragLeave={onDrop ? () => setOver(false) : undefined}
      onDrop={
        onDrop
          ? (e) => {
              setOver(false);
              const material = e.dataTransfer.getData(MATERIAL_DRAG_TYPE);
              const mine = e.dataTransfer.getData(MINE_DRAG_TYPE);
              if (!material && !mine) return;
              e.preventDefault();
              onDrop(lane.id, row, material ? { material } : { mine });
            }
          : undefined
      }
      onPointerDown={press?.onPointerDown}
      onPointerMove={press?.onPointerMove}
      onPointerUp={press?.onPointerUp}
      onPointerCancel={press?.onPointerCancel}
      onPointerLeave={press?.onPointerLeave}
      onContextMenu={press?.onContextMenu}
      onClick={() => {
        if (press?.consumeHold()) return;
        onTap(lane, row);
      }}
      aria-label={stoppable ? `Stop ${lane.name}` : `Empty slot on ${lane.name}, scene ${row + 1}`}
      aria-pressed={editable ? selected : undefined}
      title={
        stoppable
          ? `Stop ${lane.name} — on the next line`
          : `${lane.name} has no clip in scene ${row + 1} — launching this scene stops it`
      }
      className={cn(
        "flex items-center justify-center rounded-xl border border-dashed border-white/[0.07] bg-white/[0.015] text-muted-foreground/40",
        TOUCH,
        stoppable && "hover:text-foreground",
        selected && "border-solid border-white/60 bg-white/[0.06]",
        over && "border-solid border-primary bg-[color-mix(in_srgb,var(--primary)_14%,transparent)] text-primary",
      )}
      style={{ height }}
    >
      {over ? <span className="text-[11px] font-medium">Drop</span> : stoppable && <Square className="size-2.5 fill-current" />}
    </button>
  );
});

const Pad = memo(function Pad({
  clip,
  lane,
  laneIndex,
  row,
  state,
  dimmed,
  height,
  waveform,
  selected,
  editMode,
  editable,
  onTap,
  onHold,
}: {
  clip: LaneClip;
  lane: Lane;
  laneIndex: number;
  row: number;
  /** Launched, or still sounding out a queued stop. */
  state: "on" | "stopping" | null;
  dimmed: boolean;
  height: number;
  waveform?: PadWaveformSource;
  selected: boolean;
  editMode: boolean;
  editable: boolean;
  onTap: (clip: LaneClip, lane: Lane, row: number) => void;
  onHold: (sel: PadSelection) => void;
}) {
  const hold = useHold(() => onHold({ kind: "pad", clipId: clip.id, laneId: lane.id, row }));
  const press = editable ? hold : null;
  const color = clipColor(clip.color, laneIndex);
  // A playing pad is the lane's, whatever else; anything else is a launch,
  // unless there is nothing to sound (`slotTapAction`'s third answer).
  const inert = slotTapAction(clip, lane, {}) === "inert";
  const on = state === "on";
  // Still sounding, already told to stop. Not `playing`, so a tap on it is a
  // fresh launch — which is what Live does: it takes the lane back.
  const stopping = state === "stopping";
  const lit = on || stopping;
  const ghost = clip.ghost === true;
  const name = cleanClipName(clip.name ?? "") || `Clip ${row + 1}`;

  return (
    <button
      type="button"
      onPointerDown={press?.onPointerDown}
      onPointerMove={press?.onPointerMove}
      onPointerUp={press?.onPointerUp}
      onPointerCancel={press?.onPointerCancel}
      onPointerLeave={press?.onPointerLeave}
      onContextMenu={press?.onContextMenu}
      onClick={() => {
        if (press?.consumeHold()) return;
        onTap(clip, lane, row);
      }}
      disabled={inert && !editMode}
      aria-pressed={editable ? selected : undefined}
      // The rAF loop finds lit pads by this and writes `--p` / data-queued.
      data-live-lane={on ? lane.id : undefined}
      data-queued={on ? "true" : undefined}
      title={
        editMode
          ? `${name} — tap to select, double-tap to edit`
          : inert
            ? `${name} — no audio object, so there is nothing to launch`
            : stopping
              ? `${name} — stopping on the next line; tap to keep it`
              : ghost
                ? `${name} on ${lane.name} — a loop from the timeline. Hold to select it`
                : on
                  ? `${name} — stop ${lane.name} on the next line`
                  : `${name} on ${lane.name} — takes the track on the next line. Hold to edit`
      }
      className={cn(
        "group relative flex flex-col justify-between overflow-hidden rounded-xl border p-3 text-left",
        TOUCH,
        "transition-[transform,box-shadow,background-color] duration-100 active:scale-[0.97]",
        ghost && "border-dashed",
        dimmed && "opacity-30",
        inert && "cursor-default opacity-40",
        on && "data-[queued=true]:animate-pulse",
        stopping && "animate-pulse",
        selected && "outline outline-2 outline-offset-2 outline-white",
      )}
      style={{
        height,
        backgroundColor: lit ? `${color}33` : `${color}12`,
        borderColor: lit ? `${color}99` : ghost ? `${color}55` : "rgba(255,255,255,0.06)",
        boxShadow: on ? `0 0 0 2px ${color}, 0 0 26px -6px ${color}` : stopping ? `0 0 0 1px ${color}` : undefined,
      }}
    >
      <div className="flex w-full items-center justify-between gap-1">
        <span className={cn("truncate text-[13px] font-semibold", lit ? "text-foreground" : "text-foreground/75")} title={clip.name}>
          {name}
        </span>
        <span className="shrink-0 font-mono text-[10px]" style={{ color: lit ? color : "rgba(255,255,255,0.3)" }}>
          {on ? (
            <>
              <span className="hidden group-data-[queued=true]:inline">queued</span>
              <span className="group-data-[queued=true]:hidden">playing</span>
            </>
          ) : stopping ? (
            "stopping"
          ) : (
            lengthLabel(clip.duration)
          )}
        </span>
      </div>
      {waveform && clip.kind !== "midi" && (
        <PadWaveform clip={clip} lane={lane} source={waveform} color={color} lit={lit} on={on} />
      )}
      {on && (
        <span
          className="absolute bottom-0 left-0 h-[3px] w-full origin-left group-data-[queued=true]:opacity-0"
          style={{ backgroundColor: color, transform: "scaleX(var(--p, 0))" }}
        />
      )}
    </button>
  );
});

const PAD_WAVE_H = 30;

/**
 * The pad's clip as a double-sided waveform — the loop a launch plays, not a
 * picture of one. Drawn twice, once dim and once in the clip's colour, and the
 * bright copy is revealed up to the playhead with a CSS clip driven by the
 * pad's `--p`: the ring moving every frame costs a style change, never a
 * redraw and never a render.
 */
const PadWaveform = memo(function PadWaveform({
  clip,
  lane,
  source,
  color,
  lit,
  on,
}: {
  clip: LaneClip;
  lane: Lane;
  source: PadWaveformSource;
  color: string;
  lit: boolean;
  on: boolean;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const dim = useRef<HTMLCanvasElement | null>(null);
  const bright = useRef<HTMLCanvasElement | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const peaks = width > 0 ? source.peaksFor(clip, lane, width) : null;
  const { request } = source;
  useEffect(() => {
    request(clip, lane);
  }, [request, clip, lane]);
  useEffect(() => {
    if (!peaks) return;
    for (const [canvas, fill] of [
      [dim.current, lit ? `${color}66` : `${color}88`],
      [bright.current, color],
    ] as const) {
      if (!canvas) continue;
      const g = prepareCanvas(canvas, width, PAD_WAVE_H);
      if (g) drawEnvelope(g, peaks, width, PAD_WAVE_H, fill);
    }
  }, [peaks, width, color, lit]);

  return (
    <div ref={box} className="pointer-events-none relative mt-auto w-full" style={{ height: PAD_WAVE_H }} aria-hidden>
      {peaks ? (
        <>
          <canvas ref={dim} className="absolute inset-0" style={{ width, height: PAD_WAVE_H }} />
          <canvas
            ref={bright}
            className="absolute inset-0"
            style={{
              width,
              height: PAD_WAVE_H,
              clipPath: on ? "inset(0 calc((1 - var(--p, 0)) * 100%) 0 0)" : "inset(0 100% 0 0)",
            }}
          />
        </>
      ) : (
        // Still fetching: the centre line the waveform will grow out of.
        <div className="absolute inset-x-0 top-1/2 h-px" style={{ backgroundColor: `${color}44` }} />
      )}
    </div>
  );
});

/** A clip's length in bars, where it is a clean number of them. */
function lengthLabel(durationBeats: number): string {
  const bars = durationBeats / 4;
  if (bars >= 1) {
    const rounded = Math.round(bars * 100) / 100;
    return `${rounded % 1 === 0 ? rounded : rounded.toFixed(2)} bar${rounded === 1 ? "" : "s"}`;
  }
  return `${Math.round(durationBeats * 100) / 100} beat${durationBeats === 1 ? "" : "s"}`;
}

export function parseQuantize(value: string): PadQuantize {
  if (value === "bar" || value === "beat" || value === "none") return value;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : "bar";
}
